/**
 * Client-side tracking data loader — ported from
 * app/templates/game_match_summary/index.html (TrackingDataCache, ~lines
 * 2139-2330) for React migration Pass 11.
 *
 * Progressive loading with WASM, two modes:
 *
 * DIRECT (preferred): asks the API for a presigned R2 URL
 * (GET /game-match-summary/tracking/<code>/parquet-url) and reads
 * results/tracking_data.parquet straight from R2 with HTTP Range requests —
 * parquet-wasm's ParquetFile.fromUrl fetches the footer once, then exactly
 * one ~2 MB row group per 9,000-frame chunk. No Flask in the hot path. Only
 * entered when the file's layout supports it (multiple row groups, frames
 * dense from 0, verified against the footer and the first group at init).
 *
 * ENDPOINT (fallback): the original path — fetches flattened chunks from
 * GET /game-match-summary/tracking/<code>/coordinates.parquet?start=&end=
 * and decodes with the sync readParquet. Used for legacy single-row-group
 * files, when the direct fetch fails (e.g. R2 CORS not configured for
 * Range, presigned URL expired mid-session), or when the presign endpoint
 * 404s. A direct-mode failure demotes the session to this mode; both table
 * shapes coexist in the cache and getFrameData handles each.
 *
 * Data stays in Apache Arrow columnar tables (~18 bytes/row) instead of JS
 * objects (~72 bytes/row), converting rows to JS on-demand per frame.
 *
 * parquet-wasm and apache-arrow are dynamic imports from CDN URLs — NOT npm
 * dependencies. The bundle never carries them; the browser fetches them once
 * per session. parquet-wasm is pinned to 0.7.2: the 0.6.0 build the template
 * used classifies *.r2.cloudflarestorage.com URLs as Amazon S3 and cannot
 * open them (its S3 feature is compiled out); 0.7.2 reads them over plain
 * ranged HTTP.
 */

import { pathSeg } from '../../lib/urls'

export interface TrackedPlayer {
  team: number
  jersey: number
  x: number
  y: number
  speed?: number
}

export interface TrackedBall {
  x: number
  y: number
  z: number
  speed?: number
  // Possession metadata
  ballContact?: string
  ballOwner?: string
  ballStatus?: string
}

/**
 * One frame as the canvas consumes it. Frames can come from this cache or
 * from GET /workbench/frames/<frame>/<count> (a bare array of the same
 * shape); frames that passed through the formations pipeline additionally
 * carry formation/assignment fields, read by the auto-telestration layers.
 */
export interface TrackingFrame {
  frame: number
  players: TrackedPlayer[]
  ball: TrackedBall | null
  phase: number
  referees: unknown[]
  formation_team_0?: string
  formation_team_1?: string
  assignments_team_0?: number[]
  assignments_team_1?: number[]
}

// CDN URLs — identical to the template's (extra_head script tag + dynamic
// imports). Non-literal specifiers so neither Vite nor TS tries to resolve
// them at build time.
const PARQUET_WASM_URL = 'https://unpkg.com/parquet-wasm@0.7.2/esm/parquet_wasm.js'
const ARROW_URL = 'https://cdn.jsdelivr.net/npm/apache-arrow@latest/+esm'

// Minimal typings for the two CDN modules (only the surface we call).
interface ParquetWasmTable {
  intoIPCStream(): Uint8Array
}
interface ParquetWasmRowGroupMetaData {
  numRows(): number
}
interface ParquetWasmFileMetaData {
  numRows(): number
}
interface ParquetWasmMetaData {
  numRowGroups(): number
  rowGroup(i: number): ParquetWasmRowGroupMetaData
  fileMetadata(): ParquetWasmFileMetaData
}
interface ParquetWasmFileHandle {
  metadata(): ParquetWasmMetaData
  read(options?: { rowGroups?: number[] }): Promise<ParquetWasmTable>
  free?(): void
}
interface ParquetWasmModule {
  default(): Promise<unknown>
  readParquet(bytes: Uint8Array): ParquetWasmTable
  ParquetFile: { fromUrl(url: string): Promise<ParquetWasmFileHandle> }
}
interface ArrowColumn {
  get(rowIdx: number): unknown
}
interface ArrowRow {
  toJSON(): Record<string, unknown>
}
interface ArrowTable {
  numRows: number
  getChild(name: string): ArrowColumn | null
  get(rowIdx: number): ArrowRow
}
interface ArrowModule {
  tableFromIPC(stream: Uint8Array): ArrowTable
}

/** Arrow's toJSON leaves nested list children as Vectors (length/get) rather
 *  than plain arrays; struct elements are StructRows needing their own
 *  toJSON. These two normalize either representation. */
function listItems(v: unknown): unknown[] {
  if (!v) return []
  if (Array.isArray(v)) return v
  const vec = v as { length?: number; get?: (i: number) => unknown; toArray?: () => unknown[] }
  if (typeof vec.toArray === 'function') return vec.toArray()
  if (typeof vec.get === 'function' && typeof vec.length === 'number') {
    return Array.from({ length: vec.length }, (_, i) => vec.get!(i))
  }
  return []
}
function plainRecord(o: unknown): Record<string, unknown> {
  const r = o as { toJSON?: () => Record<string, unknown> }
  return typeof r?.toJSON === 'function' ? r.toJSON!() : (o as Record<string, unknown>)
}

export interface TrackingCacheStats {
  cachedFrameCount: number
  totalRowCount: number
  estimatedMemoryMB: number
  savedMemoryMB: number
  loadProgress: number
  isInitialLoaded: boolean
  isFullyLoaded: boolean
  tableCount: number
}

// Tracking Data Cache - Progressive loading with WASM
// Stores data in efficient Arrow columnar format, converts to JS on-demand
export class TrackingDataCache {
  arrowTables: ArrowTable[] = [] // Store Arrow tables directly (efficient columnar format)
  frameIndex = new Map<number, { tableIdx: number; rowIdx: number }[]>()
  loadProgress = 0 // 0-100%
  isInitialLoaded = false // First 5 min ready
  isFullyLoaded = false // Everything ready
  gameCode: string | null = null
  parquetWasm: ParquetWasmModule | null = null
  arrow: ArrowModule | null = null
  /** Pass 11 addition: lets destroy() stop the background chunk loop when the
   *  React page unmounts (the template page died with the document). */
  private cancelled = false
  /** When set, the background loader skips ahead to this chunk first. */
  private priorityChunkStart: number | null = null
  /** LRU of recently converted frames — avoids repeated Arrow→JS toJSON()
   *  calls during 30fps playback (750 row conversions/s otherwise). Sized
   *  for its READERS, not one of them: the team-split view runs three 1 Hz
   *  history scanners at once (ball heat ~120 + comet ~60 + zone occupancy
   *  ~120, columns sharing frames) BESIDE the rAF playhead readers — 128
   *  entries meant every scan evicted the previous scan and the playhead,
   *  re-converting permanently (SDET #374 r2). ~4 KB a frame; 512 ≈ 2 MB. */
  private convertedCache = new Map<number, TrackingFrame>()
  private readonly CONVERT_CACHE_SIZE = 512
  /** One network fetch per chunk, however many callers want it: the seek
   *  handler, loadFrames and the background loop all funnel through
   *  loadChunkOnce, so a slow chunk is awaited, never re-fetched. */
  private inflightChunks = new Map<number, Promise<boolean>>()
  /** The real match length when the caller knows it; the old hardcoded
   *  162000 (90 min) left the tail of longer matches — most of ours run
   *  200k+ frames — to the JSON fallback until someone seeked into it.
   *  In direct mode the parquet footer's row count overrides both. */
  private totalFrames = 162000
  /** 'direct' = ranged row-group reads from R2; 'endpoint' = flattened
   *  chunks from the coordinates endpoint. Demoted to 'endpoint' on any
   *  direct-read failure (bad layout, CORS, expired URL). */
  private mode: 'direct' | 'endpoint' = 'endpoint'
  private parquetFile: ParquetWasmFileHandle | null = null
  /** Frames per chunk. In direct mode this is the file's own row-group size
   *  read from the footer; in endpoint mode the historical 9,000. */
  private chunkFrames = 9000

  async initialize(gameCode: string, totalFrames?: number): Promise<void> {
    this.gameCode = gameCode
    if (totalFrames && totalFrames > 0) this.totalFrames = totalFrames

    try {
      // Dynamically import parquet-wasm and Apache Arrow
      const parquetModule = (await import(/* @vite-ignore */ PARQUET_WASM_URL)) as ParquetWasmModule
      await parquetModule.default() // Initialize WASM
      this.parquetWasm = parquetModule

      const arrowModule = (await import(/* @vite-ignore */ ARROW_URL)) as ArrowModule
      this.arrow = arrowModule

      let directReady = false
      try {
        directReady = await this.initializeDirect(gameCode)
      } catch (error) {
        console.warn('Direct parquet read unavailable, using chunk endpoint:', error)
      }

      if (!directReady) {
        // Load frames 0-9000 (first 5 minutes at 30fps)
        const url = `/api/v1/game-match-summary/tracking/${pathSeg(gameCode)}/coordinates.parquet?start=0&end=9000`
        const buffer = await fetch(url, { credentials: 'same-origin' }).then((r) => r.arrayBuffer())

        // Convert: parquet bytes -> WASM table -> IPC stream -> Arrow table
        const wasmTable = this.parquetWasm.readParquet(new Uint8Array(buffer))
        const ipcStream = wasmTable.intoIPCStream()
        const arrowTable = this.arrow.tableFromIPC(ipcStream)

        this.cacheFrames(arrowTable)
      }

      this.isInitialLoaded = true

      // Start background loading
      void this.loadRest(gameCode)
    } catch (error) {
      console.error('Failed to initialize tracking cache:', error)
      console.warn('Falling back to API-based tracking queries')
    }
  }

  /**
   * Try to enter direct mode: presign, open the file over ranged HTTP, and
   * verify the layout actually supports chunked reads before committing.
   * Returns false (leaving endpoint mode untouched) when any precondition
   * fails; the first row group is loaded and cached as a side effect of the
   * density check, so a successful return has already done chunk 0's work.
   */
  private async initializeDirect(gameCode: string): Promise<boolean> {
    const resp = await fetch(
      `/api/v1/game-match-summary/tracking/${pathSeg(gameCode)}/parquet-url`,
      { credentials: 'same-origin' },
    )
    if (!resp.ok) return false
    const body = (await resp.json()) as { success?: boolean; url?: string }
    if (!body.success || !body.url) return false

    const pf = await this.parquetWasm!.ParquetFile.fromUrl(body.url)
    const md = pf.metadata()
    const numGroups = md.numRowGroups()
    if (numGroups < 2) return false // legacy single-group file: a ranged read would fetch all of it

    const groupRows = md.rowGroup(0).numRows()
    const totalRows = md.fileMetadata().numRows()
    if (!groupRows || !totalRows) return false

    // Group-index arithmetic (frame -> row group) is only valid when
    // FrameCount is dense from 0, i.e. row i IS frame i. Check it against
    // the first group's real values rather than trusting the writer.
    const wasmTable = await pf.read({ rowGroups: [0] })
    const table = this.arrow!.tableFromIPC(wasmTable.intoIPCStream())
    const fc = table.getChild('FrameCount')
    if (
      !fc ||
      table.numRows !== groupRows ||
      Number(fc.get(0)) !== 0 ||
      Number(fc.get(table.numRows - 1)) !== table.numRows - 1
    ) {
      return false
    }

    this.parquetFile = pf
    this.chunkFrames = groupRows
    this.totalFrames = totalRows
    this.mode = 'direct'
    this.cacheFrames(table)
    return true
  }

  async loadRest(_gameCode: string): Promise<void> {
    try {
      // Load rest in chunks (chunkFrames = one row group in direct mode,
      // 9000 frames = 5 min at 30fps otherwise), through to the match's
      // actual last frame.
      const cf = this.chunkFrames
      const end = Math.ceil(this.totalFrames / cf) * cf
      for (let start = cf; start < end; start += cf) {
        if (this.cancelled) return

        // If a seek set a priority chunk, load it first so the playhead
        // doesn't fall back to JSON while we're still at an earlier window.
        if (this.priorityChunkStart !== null) {
          const pStart = this.priorityChunkStart
          this.priorityChunkStart = null
          await this.loadChunkOnce(pStart)
        }

        if (this.frameIndex.has(start)) {
          // Already loaded (e.g. by a priority request) — skip.
          this.loadProgress = Math.floor((start / end) * 100)
          continue
        }

        await this.loadChunkOnce(start)
        this.loadProgress = Math.floor((start / end) * 100)

        // Don't overwhelm browser - small delay between chunks
        await this.sleep(200)
      }

      this.isFullyLoaded = true
    } catch (error) {
      console.error('Error loading rest of tracking data:', error)
    }
  }

  /**
   * Immediately load the chunk containing `frame` if it isn't already
   * cached.  Called on seek so the WASM path serves the playhead instead of
   * falling back to the JSON endpoint.
   */
  async prioritizeRange(frame: number): Promise<void> {
    if (!this.gameCode || !this.parquetWasm) return
    const chunkStart = Math.floor(frame / this.chunkFrames) * this.chunkFrames
    if (this.frameIndex.has(chunkStart)) return // already cached

    // The background loop skips anything already fetched, so no marker is
    // needed; loadChunkOnce dedupes if it arrives here concurrently.
    await this.loadChunkOnce(chunkStart)
  }

  async loadChunk(gameCode: string, start: number, end: number): Promise<ArrowTable | null> {
    // Direct mode: one ranged GET for the row group covering this chunk.
    // Any failure (expired presigned URL, network) demotes the session to
    // the endpoint path — which then serves this and all later chunks.
    if (this.mode === 'direct' && this.parquetFile) {
      try {
        const rgIndex = Math.floor(start / this.chunkFrames)
        const wasmTable = await this.parquetFile.read({ rowGroups: [rgIndex] })
        return this.arrow!.tableFromIPC(wasmTable.intoIPCStream())
      } catch (error) {
        console.warn(`Direct row-group read failed for chunk ${start}; demoting to endpoint chunks:`, error)
        this.mode = 'endpoint'
      }
    }
    try {
      const url = `/api/v1/game-match-summary/tracking/${pathSeg(gameCode)}/coordinates.parquet?start=${start}&end=${end}`
      const buffer = await fetch(url, { credentials: 'same-origin' }).then((r) => r.arrayBuffer())

      // Convert: parquet bytes -> WASM table -> IPC stream -> Arrow table
      const wasmTable = this.parquetWasm!.readParquet(new Uint8Array(buffer))
      const ipcStream = wasmTable.intoIPCStream()
      const arrowTable = this.arrow!.tableFromIPC(ipcStream)

      return arrowTable
    } catch (error) {
      console.error(`Failed to load chunk ${start}-${end}:`, error)
      return null
    }
  }

  cacheFrames(table: ArrowTable): void {
    // Store Arrow table directly + build frame index
    // This keeps data in efficient columnar format (~80 MB)
    // vs converting to JS objects (~340 MB)
    const tableIdx = this.arrowTables.length
    this.arrowTables.push(table)

    // Build index: frame number -> list of row indices in this table
    // Use column API to extract only frame numbers (efficient!)
    // Endpoint chunks name the column 'frame' (flat rows, ~24 per frame);
    // direct row groups name it 'FrameCount' (nested rows, 1 per frame).
    const numRows = table.numRows
    const frameColumn = table.getChild('frame') ?? table.getChild('FrameCount')
    if (!frameColumn) return

    for (let rowIdx = 0; rowIdx < numRows; rowIdx++) {
      // Extract only frame number from column (no full row conversion)
      const frame = Number(frameColumn.get(rowIdx))

      if (!this.frameIndex.has(frame)) {
        this.frameIndex.set(frame, [])
      }
      this.frameIndex.get(frame)!.push({ tableIdx, rowIdx })
    }
  }

  getFrameData(frame: number): TrackingFrame | null {
    // Fast path: return already-converted frame from the LRU cache.
    const cached = this.convertedCache.get(frame)
    if (cached) {
      // Move to end (most-recently used) by re-inserting.
      this.convertedCache.delete(frame)
      this.convertedCache.set(frame, cached)
      return cached
    }

    // Check Arrow index
    const rows = this.frameIndex.get(frame)
    if (!rows) return null

    // Convert Arrow rows to JS objects on-demand (only when needed)
    const players: TrackedPlayer[] = []
    let ball: TrackedBall | null = null
    let phase = 1

    for (const { tableIdx, rowIdx } of rows) {
      const table = this.arrowTables[tableIdx]
      if (table === undefined) continue

      // Direct-mode tables are the nested source schema: one row per frame,
      // players and ball as list<struct> columns. (Both shapes can coexist
      // in one session after a mid-session demotion, so detect per table.)
      if (table.getChild('PlayerPositions')) {
        const row = table.get(rowIdx).toJSON()
        for (const p of listItems(row.PlayerPositions)) {
          const pj = plainRecord(p)
          players.push({
            team: Number(pj.Team),
            jersey: Number(pj.JerseyNumber),
            x: Number(pj.X),
            y: Number(pj.Y),
            speed: Number(pj.Speed ?? 0),
          })
        }
        const ballList = listItems(row.BallPosition)
        if (ballList.length > 0) {
          const b = plainRecord(ballList[0])
          ball = {
            x: Number(b.X),
            y: Number(b.Y),
            z: Number(b.Z ?? 0),
            speed: Number(b.Speed ?? 0),
            ballContact: b.BallContactInfo != null ? String(b.BallContactInfo) : '',
            ballOwner: b.BallOwningTeam != null ? String(b.BallOwningTeam) : '',
            ballStatus: b.BallStatus != null ? String(b.BallStatus) : '',
          }
        }
        if (row.Phase !== null && row.Phase !== undefined) {
          phase = Number(row.Phase)
        }
        continue
      }

      const row = table.get(rowIdx).toJSON()

      // Convert BigInt to Number
      const type = row.type

      if (type === 'player') {
        players.push({
          team: Number(row.team),
          jersey: Number(row.player),
          x: row.x as number,
          y: row.y as number,
          speed: row.speed as number,
        })
        // Get phase from any row (it's the same for all rows in a frame)
        if (row.phase !== null && row.phase !== undefined) {
          phase = Number(row.phase)
        }
      } else if (type === 'ball') {
        ball = {
          x: row.x as number,
          y: row.y as number,
          z: row.z as number,
          speed: row.speed as number,
          // Possession metadata
          ballContact: (row.ball_contact as string) || '',
          ballOwner: (row.ball_owner as string) || '',
          ballStatus: (row.ball_status as string) || '',
        }
        if (row.phase !== null && row.phase !== undefined) {
          phase = Number(row.phase)
        }
      }
    }

    const result: TrackingFrame = {
      frame: frame,
      players: players,
      ball: ball,
      phase: phase,
      referees: [], // Referees not cached
    }

    // Store in LRU cache; evict oldest if full.
    this.convertedCache.set(frame, result)
    if (this.convertedCache.size > this.CONVERT_CACHE_SIZE) {
      // Map iteration order is insertion order — first key is the oldest.
      const oldest = this.convertedCache.keys().next().value
      if (oldest !== undefined) this.convertedCache.delete(oldest)
    }

    return result
  }

  /** Fetch-and-cache one chunk exactly once. Concurrent callers (seek
   *  handler, loadFrames, background loop) await the same promise; a chunk
   *  that failed clears its slot so a later attempt can retry. */
  private loadChunkOnce(chunkStart: number): Promise<boolean> {
    if (this.frameIndex.has(chunkStart)) return Promise.resolve(true)
    const existing = this.inflightChunks.get(chunkStart)
    if (existing) return existing
    const gameCode = this.gameCode
    if (!gameCode) return Promise.resolve(false)
    const p = this.loadChunk(gameCode, chunkStart, chunkStart + 9000)
      .then((chunk) => {
        if (chunk) this.cacheFrames(chunk)
        return chunk != null
      })
      .catch(() => false)
      .finally(() => { this.inflightChunks.delete(chunkStart) })
    this.inflightChunks.set(chunkStart, p)
    return p
  }

  hasCachedFrame(frame: number): boolean {
    return this.frameIndex.has(frame)
  }

  getStats(): TrackingCacheStats {
    // Calculate approximate memory usage and cache statistics
    const cachedFrameCount = this.frameIndex.size
    const totalRowCount = this.arrowTables.reduce((sum, table) => sum + table.numRows, 0)

    // Estimate memory: Arrow tables are ~18 bytes/row (columnar compression)
    // vs ~72 bytes/row for JS objects
    const estimatedMemoryMB = Math.round((totalRowCount * 18) / (1024 * 1024))
    const savedMemoryMB = Math.round((totalRowCount * 54) / (1024 * 1024)) // Would have been 72, now 18

    return {
      cachedFrameCount,
      totalRowCount,
      estimatedMemoryMB,
      savedMemoryMB,
      loadProgress: this.loadProgress,
      isInitialLoaded: this.isInitialLoaded,
      isFullyLoaded: this.isFullyLoaded,
      tableCount: this.arrowTables.length,
    }
  }

  sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  /** Stop background loading and release table references (page unmount). */
  destroy(): void {
    this.cancelled = true
    this.arrowTables = []
    this.frameIndex.clear()
    this.convertedCache.clear()
    this.inflightChunks.clear()
    try {
      this.parquetFile?.free?.()
    } catch {
      // WASM handle may already be freed; nothing to release either way
    }
    this.parquetFile = null
  }
}
